import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { ArrowLeft, Calendar, Tag, BookOpen, ChevronRight, Rss } from 'lucide-react';
import { loadAllArticles, loadArticleById, WorkbookArticle, getAllTags } from '../workbook';
import Navbar from '../components/Navbar';

/**
 * SEO 组件 - 动态设置页面标题和 meta 标签
 */
const ArticleSEO: React.FC<{ article: WorkbookArticle | null }> = ({ article }) => {
  useEffect(() => {
    if (article) {
      // 设置页面标题
      document.title = `${article.title} | Workbook | Workbees`;
      
      // 设置 meta description
      const description = article.content.slice(0, 200).replace(/<[^>]*>/g, '').replace(/[#*_`]/g, '').trim();
      updateMetaTag('description', description);
      updateMetaTag('og:title', article.title);
      updateMetaTag('og:description', description);
      updateMetaTag('og:type', 'article');
      updateMetaTag('og:url', `https://www.workbees.space/workbook?id=${article.id}`);
      updateMetaTag('article:published_time', article.date);
      updateMetaTag('article:tag', article.tags.join(','));
    } else {
      // 恢复默认标题
      document.title = 'Workbook | Workbees';
      updateMetaTag('description', '探索人工智能时代的工作方式、思维模式与行业趋势');
      updateMetaTag('og:title', 'Workbook | Workbees');
      updateMetaTag('og:description', '探索人工智能时代的工作方式、思维模式与行业趋势');
    }
  }, [article]);

  return null;
};

/**
 * 更新 meta 标签
 */
function updateMetaTag(name: string, content: string) {
  let meta = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`) as HTMLMetaElement;
  if (!meta) {
    meta = document.createElement('meta');
    if (name.startsWith('og:') || name.startsWith('article:')) {
      meta.setAttribute('property', name);
    } else {
      meta.setAttribute('name', name);
    }
    document.head.appendChild(meta);
  }
  meta.content = content;
}

const Workbook: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState<WorkbookArticle[]>([]);
  const [currentArticle, setCurrentArticle] = useState<WorkbookArticle | null>(null);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [loading, setLoading] = useState(true);

  // 上报 Workbook 页面访问
  useEffect(() => {
    const reportWorkbookView = async () => {
      try {
        await fetch('/api/workbook/stats/view', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
      } catch (error) {
        // 静默失败，不影响用户体验
        console.debug('Workbook view stats report failed:', error);
      }
    };
    
    reportWorkbookView();
  }, []);

  // 从 URL 查询参数读取文章 ID
  useEffect(() => {
    const articleId = searchParams.get('id');
    if (articleId) {
      loadArticleById(articleId).then(article => {
        if (article) {
          setCurrentArticle(article);
        }
      });
    }
  }, [searchParams]);

  // 加载所有文章和标签
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const [loadedArticles, tags] = await Promise.all([
        loadAllArticles(),
        getAllTags()
      ]);
      setArticles(loadedArticles);
      setAllTags(tags);
      setLoading(false);
    };
    init();
  }, []);

  const handleArticleClick = (article: WorkbookArticle) => {
    setCurrentArticle(article);
    setSearchParams({ id: article.id });
  };

  const handleBackToList = () => {
    setCurrentArticle(null);
    setSearchParams({});
  };

  const filteredArticles = selectedTag
    ? articles.filter(a => a.tags.includes(selectedTag))
    : articles;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">加载中...</div>
      </div>
    );
  }

  // 文章详情页
  if (currentArticle) {
    return (
      <div className="min-h-screen bg-slate-50 pt-14">
        {/* SEO 组件 */}
        <ArticleSEO article={currentArticle} />
        
        {/* Navbar - 共享组件 */}
        <Navbar />

        {/* Article Content */}
        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* 返回按钮 */}
          <button
            onClick={handleBackToList}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-indigo-600 transition-colors mb-6"
          >
            <ArrowLeft size={16} />
            返回列表
          </button>

          <article className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 sm:p-8">
            {/* Article Meta */}
            <div className="mb-6 pb-6 border-b border-slate-100">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-4">
                {currentArticle.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <Calendar size={14} />
                  {currentArticle.date}
                </span>
                {currentArticle.tags.length > 0 && (
                  <span className="flex items-center gap-1">
                    <Tag size={14} />
                    {currentArticle.tags.join(', ')}
                  </span>
                )}
              </div>
            </div>

            {/* Article Body */}
            <div className="prose prose-slate max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  p: ({ children }) => <p className="my-4 leading-relaxed">{children}</p>,
                  h1: ({ children }) => <h1 className="text-3xl font-bold mt-8 mb-4">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-2xl font-bold mt-6 mb-3">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-xl font-bold mt-4 mb-2">{children}</h3>,
                }}
              >
                {currentArticle.content}
              </ReactMarkdown>
            </div>
          </article>
        </main>
      </div>
    );
  }

  // 文章列表页
  return (
    <div className="min-h-screen bg-slate-50 pt-14">
      {/* Navbar - 共享组件 */}
      <Navbar />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar - Tags */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 sticky top-20">
              <h2 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                <Tag size={16} />
                标签
              </h2>
              <div className="space-y-1">
                <button
                  onClick={() => setSelectedTag('')}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                    selectedTag === ''
                      ? 'bg-indigo-50 text-indigo-600'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  全部文章 ({articles.length})
                </button>
                {allTags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => setSelectedTag(tag)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                      selectedTag === tag
                        ? 'bg-indigo-50 text-indigo-600'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {tag} ({articles.filter(a => a.tags.includes(tag)).length})
                  </button>
                ))}
              </div>
              
              {/* RSS 订阅 */}
              <div className="mt-6 pt-4 border-t border-slate-100">
                <a
                  href="/rss.xml"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-slate-500 hover:text-orange-500 transition-colors"
                >
                  <Rss size={16} />
                  <span>RSS 订阅</span>
                </a>
              </div>
            </div>
          </aside>

          {/* Article List */}
          <div className="flex-1">
            {filteredArticles.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <BookOpen size={48} className="mx-auto mb-4 opacity-50" />
                <p>暂无文章</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredArticles.map(article => (
                  <article
                    key={article.id}
                    onClick={() => handleArticleClick(article)}
                    className="bg-white rounded-lg shadow-sm border border-slate-200 p-5 cursor-pointer hover:shadow-md hover:border-indigo-200 transition-all group"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h2 className="text-lg font-semibold text-slate-800 group-hover:text-indigo-600 transition-colors mb-2">
                          {article.title}
                        </h2>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            {article.date}
                          </span>
                          {article.tags.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Tag size={14} />
                              {article.tags.join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight
                        size={20}
                        className="text-slate-300 group-hover:text-indigo-400 transition-colors flex-shrink-0 mt-1"
                      />
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Workbook;
